import { AppEnum, AppEnumHelper, dateCompare, MandateEnums, SortModelHelper, stringCompare } from "@/Shared";
import {
    ContactModel,
    CityModel,
    SectorModel,
    MandateModel,
    PropertyModel,
    UserModel,
    TaskModel,
    TransactionModel,
    LeaseModel,
    PersonModel,
    NeedModel,
    CONTACT_TYPES,
} from "@/Domains";

// Utility function to sort objects by their "label" property
const sortByLabel = (a: { label: string }, b: { label: string }) => {
    return stringCompare(a.label, b.label);
};


const sortByMinMax = (a: AppEnum, b: AppEnum) => {
    const minDif = (a.min ?? Number.MIN_SAFE_INTEGER) - (b.min ?? Number.MIN_SAFE_INTEGER);
    const maxDif = (a.max ?? Number.MAX_SAFE_INTEGER) - (b.max ?? Number.MAX_SAFE_INTEGER);
    return minDif === 0 ? maxDif : minDif;
};



const formatName = (person?: PersonModel) => {
    return (person?.lastname ?? "").toUpperCase() + " " + (person?.firstname ?? "");
}

export class OptionHelper {

    static contactOptions(contacts: ContactModel[]): AppEnum[] {

        return contacts.map(contact => {
            return {
                key: String(contact.id),
                value: contact.id,
                label: formatName(contact.person)
            }
        }).sort(sortByLabel);
    }

    static ownerOptions(contactList: ContactModel[]) {
        const extractBuyers = contactList.filter(
            contact => [
                CONTACT_TYPES.OWNER,
                CONTACT_TYPES.OWNER_PROSPECT
            ].includes(contact.contactType as CONTACT_TYPES)
        );

        return OptionHelper.contactOptions(extractBuyers);
    }

    static propertyOptions(propertyList: PropertyModel[], propertyTypeEnum?: AppEnum[]) {

        return propertyList.map(property => {
            return {
                key: String(property.id),
                value: String(property.id),
                label: propertyTypeEnum 
                ? 
                AppEnumHelper.getLabel(propertyTypeEnum, property.propertyType) + " - (ID : " + property.id + ")"
                : "ID : " + property.id
            }
        }).sort(sortByLabel);
    }

    static needOptions(needList: NeedModel[], propertyTypeEnum: AppEnum[], needStatusEnum: AppEnum[]) {

        return needList
            .sort((n1, n2) => SortModelHelper.sortNeed(needStatusEnum, n1, n2))
            .map(need => {
                return {
                    key: String(need.id),
                    value: String(need.id),
                    label: AppEnumHelper.getLabel(propertyTypeEnum, need.propertyType) + " - (ID : " + need.id + ")"
                }
            });
    }

    static cityOptions(cities: CityModel[]): AppEnum[] {

        return cities.map(city => {
            return {
                key: String(city.id),
                value: String(city.id),
                label: city.name
            }
        }).sort(sortByLabel);
    }

    static sectorOptions(sectors: SectorModel[]): AppEnum[] {

        return sectors.map(sector => {
            return {
                key: String(sector.id),
                value: String(sector.id),
                label: sector.name
            }
        }).sort(sortByLabel);
    }


    static userOptions = (users: UserModel[]) => {
        return users.map(user => {

            return {
                key: String(user.id),
                value: String(user.id),
                label: formatName(user.person)
            }
        }).sort(sortByLabel);
    }

    static leaseOptions(leases: LeaseModel[]): AppEnum[] {

        return leases
            .sort((l1, l2) => dateCompare(l2.createdAt ?? "", l1.createdAt ?? ""))
            .map(lease => {
                return {
                    key: String(lease.id),
                    value: String(lease.id),
                    label: lease.leaseType + ' - (ID : ' + lease.id + ')'
                }
            });
    }

    static mandateOptions(mandates: MandateModel[], mandateEnums: MandateEnums): AppEnum[] {
        return mandates.map(mandate => {
            return {
                key: String(mandate.id),
                value: String(mandate.id),
                label: (mandateEnums.OperationTypeEnum.find(op => mandate.operationType === op.value)?.label ?? "Mandat") + ' - (ID : ' + mandate.id + ')'
            }
        }).sort(sortByLabel);
    }

    static transactionOptions(transactions: TransactionModel[]): AppEnum[] {
        return transactions
            .sort((t1, t2) => dateCompare(t2.createdAt ?? "", t1.createdAt ?? ""))
            .map(transaction => {
                return {
                    key: String(transaction.id),
                    value: String(transaction.id),
                    label: formatName(transaction.client?.person) + ' - (ID : ' + transaction.id + ')'
                }
            }).sort(sortByLabel);
    }


    static taskOptions(taskList: TaskModel[], taskCategoryEnum: AppEnum[]) {

        return taskList.map(task => {
            return {
                key: String(task.id),
                value: String(task.id),
                label: (taskCategoryEnum.find(cat => cat.value === task.category)?.label ?? task.category) + ' - ' + task.label
            }
        }).sort(sortByLabel);
    }

    static sortByLabel(options: AppEnum[]) {
        const grouped = options.reduce(
            (acc, opt) => {
                if (opt.value === 'other') {
                    acc.other.push(opt);
                } else {
                    acc.others.push(opt);
                }
                return acc;
            },
            { other: [] as AppEnum[], others: [] as AppEnum[] }
        );
        const sortedOthers = grouped.others.sort(sortByLabel);
        return [...sortedOthers, ...grouped.other];
    }

    static sortByMinMax(options: AppEnum[]) {
        return [...options].sort(sortByMinMax);
    }
}